import { Howl, Howler } from 'howler';

export class AudioManager {
  private static instance: AudioManager;
  private sounds: Map<string, Howl> = new Map();
  private muted: boolean = false;
  private volume: number = 0.5;
  
  private constructor() {
    this.createPlaceholderSounds();
  }
  
  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }
  
  private createPlaceholderSounds(): void {
    this.sounds.set('spin', this.createTone(200, 0.1, 'sine'));
    this.sounds.set('stop', this.createTone(400, 0.05, 'sine'));
    this.sounds.set('win', this.createWinSound());
    this.sounds.set('bigWin', this.createBigWinSound());
    this.sounds.set('click', this.createTone(600, 0.05, 'sine'));
  }
  
  private createTone(frequency: number, duration: number, type: OscillatorType): Howl {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const sampleRate = audioContext.sampleRate;
    const buffer = audioContext.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < buffer.length; i++) {
      data[i] = Math.sin(2 * Math.PI * frequency * i / sampleRate) * 0.3;
    }
    
    const wavBuffer = this.bufferToWav(buffer);
    const blob = new Blob([wavBuffer], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    
    return new Howl({
      src: [url],
      volume: this.volume,
      rate: 1.0
    });
  }
  
  private createWinSound(): Howl {
    const frequencies = [523.25, 659.25, 783.99, 1046.50];
    const duration = 0.15;
    
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const buffer = audioContext.createBuffer(1, audioContext.sampleRate * 1, audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    let offset = 0;
    frequencies.forEach((freq) => {
      const startSample = Math.floor(offset * audioContext.sampleRate);
      const endSample = Math.floor((offset + duration) * audioContext.sampleRate);
      
      for (let i = startSample; i < endSample && i < buffer.length; i++) {
        const envelope = Math.exp(-((i - startSample) / (audioContext.sampleRate * duration)));
        data[i] = Math.sin(2 * Math.PI * freq * i / audioContext.sampleRate) * 0.3 * envelope;
      }
      
      offset += duration;
    });
    
    const wavBuffer = this.bufferToWav(buffer);
    const blob = new Blob([wavBuffer], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    
    return new Howl({
      src: [url],
      volume: this.volume
    });
  }
  
  private createBigWinSound(): Howl {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const duration = 2;
    const buffer = audioContext.createBuffer(1, audioContext.sampleRate * duration, audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < buffer.length; i++) {
      const t = i / audioContext.sampleRate;
      const freq = 400 + Math.sin(t * 10) * 200;
      const envelope = Math.exp(-t / (duration * 0.5));
      
      data[i] = (
        Math.sin(2 * Math.PI * freq * t) * 0.2 +
        Math.sin(2 * Math.PI * (freq * 1.5) * t) * 0.15 +
        Math.sin(2 * Math.PI * (freq * 2) * t) * 0.1
      ) * envelope;
    }
    
    const wavBuffer = this.bufferToWav(buffer);
    const blob = new Blob([wavBuffer], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    
    return new Howl({
      src: [url],
      volume: this.volume
    });
  }
  
  private bufferToWav(buffer: AudioBuffer): ArrayBuffer {
    const numChannels = 1;
    const sampleRate = buffer.sampleRate;
    const format = 1;
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    const data = buffer.getChannelData(0);
    const samples = data.length;
    const dataSize = samples * blockAlign;
    const bufferSize = 44 + dataSize;
    
    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);
    
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, bufferSize - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    const offset = 44;
    for (let i = 0; i < samples; i++) {
      const sample = Math.max(-1, Math.min(1, data[i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset + i * 2, intSample, true);
    }
    
    return arrayBuffer;
  }
  
  public play(soundName: string): void {
    if (this.muted) return;
    
    const sound = this.sounds.get(soundName);
    if (sound) {
      sound.play();
    }
  }
  
  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.volume);
  }
  
  public mute(): void {
    this.muted = true;
    Howler.mute(true);
  }
  
  public unmute(): void {
    this.muted = false;
    Howler.mute(false);
  }
  
  public toggleMute(): boolean {
    if (this.muted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.muted;
  }
  
  public isMuted(): boolean {
    return this.muted;
  }
}